import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AddressAutocompleteComponent } from '@libraries/google-maps';
import { parseAddress, validateAddress } from '@libraries/helpers';
import { maskConfig, moneyMaskWithoutDecimal } from '@libraries/ui';
import { isDesktop } from '@libraries/ui-state';
import { select, Store } from '@ngrx/store';
import { takeWhile } from 'rxjs/operators';
import { calculateMonthlyCost, isLoading, validatePin } from '../+state';
import { PropertyAddress } from '../models';
import { CalculatorService } from '../services/calculator.service';
import { CustomEstimatedCostValidator } from '../validators/custom-estimated-cost.validator';

@Component({
  selector: 'landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  standalone: false,
})
export class LandingPageComponent implements OnInit, OnDestroy, AfterViewInit {
  moneyMaskWithoutDecimal = moneyMaskWithoutDecimal;
  maskConfig = maskConfig;
  pageForm: UntypedFormGroup;
  pin: string;
  estimatedCosts = [
    { display: '$10,000', value: 10000 },
    { display: '$20,000', value: 20000 },
    { display: '$30,000', value: 30000 },
    { display: '$40,000', value: 40000 },
    { display: '$50,000', value: 50000 },
  ];
  private alive = true;

  isDesktop$ = this.store.pipe(select(isDesktop));
  isLoading$ = this.store.pipe(select(isLoading));

  @ViewChild(AddressAutocompleteComponent, { static: true })
  addressComponent: AddressAutocompleteComponent;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private svc: CalculatorService,
    private route: ActivatedRoute
  ) {
    this.buildForm();
    this.handleCostFields();
  }

  ngOnInit() {
    this.validatePinAndAutoLoginUser();
  }

  ngAfterViewInit() {
    this.addressComponent.field.focus();
  }

  ngOnDestroy() {
    this.alive = false;
  }

  // When cost bubble is selected, we need to reset the custom cost input value and vice-versa. So, handling here accordingly.
  handleCostFields() {
    const estimatedCost = this.estimatedCostControl();
    const customEstimatedCost = this.customEstimatedCostControl();

    estimatedCost.valueChanges.pipe(takeWhile(() => this.alive)).subscribe((value) => {
      if (value) {
        customEstimatedCost.patchValue(null, { emitEvent: false });
      }
      customEstimatedCost.updateValueAndValidity({ emitEvent: false });
    });
    customEstimatedCost.valueChanges.pipe(takeWhile(() => this.alive)).subscribe((value) => {
      if (value) {
        estimatedCost.patchValue(null, { emitEvent: false });
      }
      estimatedCost.updateValueAndValidity({ emitEvent: false });
    });
  }

  validatePinAndAutoLoginUser() {
    if (this.isPinPresent()) {
      this.store.dispatch(validatePin({ payload: { pin: this.pin } }));
    }
  }

  submit() {
    this.pageForm.markAllAsTouched();
    if (this.pageForm.invalid) return;

    // Parsing and formatting the address before sending to ruby.
    const address = this.pageForm.value.address;
    const formatAddress: PropertyAddress = parseAddress(address);

    const payload = {
      address: formatAddress,
      estimated_cost: this.customEstimatedCostControl().value || this.estimatedCostControl().value,
      include_apr: true,
    };
    this.store.dispatch(calculateMonthlyCost({ payload }));
  }

  private buildForm() {
    this.pageForm = this.fb.group({
      address: [null, Validators.compose([Validators.required, validateAddress])],
      estimated_cost: [null],
      // any better fix than adding updateOn?
      custom_estimated_cost: [null, { updateOn: 'focus' }],
    });

    // We needed to do this as we need to pass the dependent control to Validator.
    this.estimatedCostControl().setValidators([
      CustomEstimatedCostValidator(this.customEstimatedCostControl()),
    ]);
    this.customEstimatedCostControl().setValidators([
      CustomEstimatedCostValidator(this.estimatedCostControl()),
      Validators.min(5000),
      Validators.max(100000000),
    ]);
  }

  estimatedCostControl(): AbstractControl {
    return this.pageForm?.get('estimated_cost');
  }

  customEstimatedCostControl(): AbstractControl {
    return this.pageForm.get('custom_estimated_cost');
  }

  isPinPresent() {
    const id = this.route.snapshot.params['id'];
    if (this.svc.cookieHasPin()) {
      this.pin = this.svc.getPin();
    } else if (id) {
      this.pin = id;
    }
    return !!this.pin;
  }
}
